import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { useConfirmation } from '../../hooks/useModals';
import { adminService } from '../../services';
import { ChevronLeft } from 'lucide-react';
import Input from '../../components/ui/Input';
import { LoadingSpinner } from '../../components/ui/Loading';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import CreateRelicModal from '../../components/modals/CreateRelicModal';

const AdminRelics = () => {
  const { user } = useAuth();
  const { notify } = useNotification();
  const { modalState: confirmState, confirm, close: closeConfirm } = useConfirmation();
  const [relics, setRelics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const itemsPerPage = 12;

  const loadRelics = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: itemsPerPage,
        search: searchTerm.trim() || undefined,
      };

      const response = await adminService.getRelics(params);
      setRelics(response.data?.relics || []);
      setTotalPages(response.data?.totalPages || 1);
      setTotalItems(response.data?.total || 0);
    } catch (error) {
      console.error('Erro ao carregar relíquias:', error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm]);

  useEffect(() => {
    if (user?.role === 'ADMIN') {
      loadRelics();
    }
  }, [user, loadRelics]);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleCreateRelic = async (relicData) => {
    try {
      await adminService.createRelic(relicData);
      await loadRelics(); // Recarregar lista
    } catch (error) {
      console.error('Erro ao criar relíquia:', error);
      throw error;
    }
  };

  const handleEditRelic = async (relic) => {
    // TODO: Implementar modal de edição de relíquia
    console.log('Editar relíquia:', relic);
  };

  const handleDeleteRelic = async (relicId) => {
    const confirmed = await confirm({
      title: 'Excluir Relíquia',
      message: 'Tem certeza que deseja excluir esta relíquia? Esta ação não pode ser desfeita.',
      confirmText: 'Excluir',
      cancelText: 'Cancelar',
      type: 'danger',
      icon: '🗑️'
    });

    if (!confirmed) return;

    try {
      await adminService.deleteRelic(relicId);
      notify.success('Relíquia excluída com sucesso!', '🗑️ Relíquia Removida');
      await loadRelics();
    } catch (error) {
      console.error('Erro ao excluir relíquia:', error);
      notify.error('Erro ao excluir relíquia. Tente novamente.', 'Erro');
    }
  };

  const getPowerColor = (power) => {
    if (power >= 90) return 'text-purple-600 font-bold';
    if (power >= 70) return 'text-orange-600 font-semibold';
    if (power >= 50) return 'text-blue-600';
    if (power >= 30) return 'text-green-600';
    return 'text-gray-400';
  };

  if (user?.role !== 'ADMIN') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl p-8 border border-red-500/20 shadow-xl max-w-md w-full text-center">
          <div className="w-20 h-20 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-5xl">🚫</span>
          </div>
          <h2 className="text-2xl font-bold text-white mb-3">
            Acesso Negado
          </h2>
          <p className="text-gray-400 mb-6">
            Apenas administradores podem acessar esta página.
          </p>
          <button
            onClick={() => window.location.href = '/app/dashboard'}
            className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-medium py-3 px-6 rounded-lg transition-all duration-300"
          >
            Voltar ao Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4 sm:p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-xl">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => window.location.href = '/app/admin'}
                className="flex items-center justify-center w-10 h-10 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-purple-500/50 rounded-lg text-gray-300 hover:text-white transition-all duration-300"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent mb-1">
                  🏺 Gerenciar Relíquias
                </h1>
                <p className="text-gray-300">
                  {totalItems > 0 ? `${totalItems} relíquias encontradas` : 'Administre as relíquias do jogo'}
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-semibold rounded-lg transition-all duration-300 shadow-lg shadow-purple-500/20 whitespace-nowrap"
            >
              + Nova Relíquia
            </button>
          </div>
        </div>

        {/* Filtros */}
        <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 shadow-xl">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Buscar Relíquias
              </label>
              <Input
                type="text"
                placeholder="Nome ou descrição..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="w-full"
              />
            </div>
          </div>
        </div>

        {/* Lista de Relíquias */}
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <LoadingSpinner size="lg" />
          </div>
        ) : relics.length === 0 ? (
          <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl p-12 border border-white/10 shadow-xl text-center">
            <div className="text-6xl mb-4">🏺</div>
            <h3 className="text-xl font-semibold text-white mb-2">
              Nenhuma relíquia encontrada
            </h3>
            <p className="text-gray-400 mb-6">
              {searchTerm ? 'Tente ajustar os filtros de busca.' : 'Comece criando sua primeira relíquia.'}
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-medium rounded-lg transition-all duration-300 shadow-lg shadow-purple-500/20"
            >
              + Criar Primeira Relíquia
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {relics.map((relic) => (
              <div 
                key={relic.id}
                className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-purple-500/50 transition-all duration-300 shadow-xl group"
              >
                {/* Header da Relíquia */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <span className="text-4xl group-hover:scale-110 transition-transform duration-300">🏺</span>
                    <div>
                      <h3 className="font-bold text-white text-lg group-hover:text-purple-400 transition-colors">
                        {relic.name}
                      </h3>
                    </div>
                  </div>
                </div>

                {/* Descrição */}
                <div className="mb-4">
                  <p className="text-sm text-gray-400 line-clamp-2 min-h-[2.5rem]">
                    {relic.description || 'Sem descrição'}
                  </p>
                </div>

                {/* Ações */}
                <div className="flex gap-2 pt-4 border-t border-white/10">
                  <button
                    onClick={() => handleEditRelic(relic)}
                    className="flex-1 px-3 py-2 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-blue-500/50 rounded-lg text-blue-400 hover:text-blue-300 text-sm font-medium transition-all duration-300"
                  >
                    ✏️ Editar
                  </button>
                  <button
                    onClick={() => handleDeleteRelic(relic.id)}
                    className="flex-1 px-3 py-2 bg-red-500/10 hover:bg-red-500/20 border border-red-500/20 hover:border-red-500/50 rounded-lg text-red-400 hover:text-red-300 text-sm font-medium transition-all duration-300"
                  >
                    🗑️ Excluir
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Paginação */}
        {totalPages > 1 && (
          <div className="bg-gradient-to-br from-slate-800/80 to-slate-900/80 backdrop-blur-xl rounded-2xl p-4 border border-white/10 shadow-xl">
            <div className="flex justify-center items-center gap-3">
              <button
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-purple-500/50 rounded-lg text-gray-300 hover:text-white transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white/5 disabled:hover:border-white/10"
              >
                ← Anterior
              </button>

              <span className="px-6 py-2 text-sm text-gray-300 bg-white/5 rounded-lg border border-white/10">
                Página <span className="font-bold text-white">{currentPage}</span> de <span className="font-bold text-white">{totalPages}</span>
                <span className="text-gray-500 ml-2">({totalItems} relíquias)</span>
              </span>

              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-purple-500/50 rounded-lg text-gray-300 hover:text-white transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white/5 disabled:hover:border-white/10"
              >
                Próxima →
              </button>
            </div>
          </div>
        )}

        {/* Modal de Criação */}
        <CreateRelicModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateRelic}
        />

        {/* Confirmation Modal */}
        <ConfirmationModal
          isOpen={confirmState.isOpen}
          onClose={closeConfirm}
          onConfirm={confirmState.onConfirm}
          title={confirmState.title}
          message={confirmState.message}
          confirmText={confirmState.confirmText}
          cancelText={confirmState.cancelText}
          type={confirmState.type}
          icon={confirmState.icon}
        />
      </div>
    </div>
  );
};

export default AdminRelics;